import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAPI from '../hooks/useAPI';
import { getProjects, getProjectErrorSummary, getProjectErrorStatus, getTechnicians } from '../services/api';

const COUNTRY_FILTER_OPTIONS = [
  { key: 'ALL', label: 'All' },
  { key: 'Israel', label: 'Israel' },
  { key: 'India', label: 'India' },
];

/**
 * Project List page — organized by Country > Region.
 * Shows robot count, 30-day error counts (Critical/High/Low),
 * and errors-per-robot ratio with green/red ranking per country.
 */

// Region mapping
const REGION_MAP_ISRAEL = {
  'נגב מערבי': ['Lotan', 'Samar', 'Neot Smadar', 'Mitzpe Ramon', 'Sde Boker', 'revivim',
    'mashabey sade', 'Beit Kama', 'Gvulot', 'Givolim', 'Yakhini', 'Ein Habsor',
    'Nachal Oz', 'Nirim', 'Nir Itzhak', 'Sufa', 'Kfar Aza', 'Pri Gan', 'Tarabin', 'Cramim',
    'Lahav', 'Arad', 'Ein Tzurim', 'bikura'],
  'צפון': ['Kfar Hanassi', 'Lavi', 'Sde Nachum', 'Kfar Blum', 'Mahanayim', 'Kidmat tzvi'],
  'מרכז': ['Rehovot'],
};

const REGION_MAP_INDIA = {
  'Rajasthan': ['Pokrhan', 'Pokhran', 'Bhadla', 'Bikaner', 'Rajasthan', 'Nokhara',
    'Dedasari', 'Nimba', 'Toga', '.'],
  'Gujarat': ['Gujarat', 'Modha', 'Hasapur'],
  'Maharashtra': ['Maharashtra', 'Pangaon', 'Dhamangaon'],
  'Karnataka': ['Koppal', 'Kudligi', 'Arni'],
  'Other India': ['India'],
};

function getRegion(country, city) {
  if (country === 'Israel') {
    for (const [region, cities] of Object.entries(REGION_MAP_ISRAEL)) {
      if (cities.includes(city)) return region;
    }
    return 'אחר';
  }
  if (country === 'India') {
    for (const [region, cities] of Object.entries(REGION_MAP_INDIA)) {
      if (cities.includes(city)) return region;
    }
    return 'Other';
  }
  return country || 'Other';
}

const COUNTRY_ORDER = ['Israel', 'India', 'United Arab Emirates'];
const COUNTRY_FLAGS = { Israel: '🇮🇱', India: '🇮🇳', 'United Arab Emirates': '🇦🇪' };

// How many projects to highlight per country
const RANK_SIZE = { Israel: 5, India: 10 };

export default function ProjectList() {
  const navigate = useNavigate();
  const { data: projData, loading: loadingProj } = useAPI(getProjects);
  const { data: errData, loading: loadingErr } = useAPI(() => getProjectErrorSummary(30));
  const { data: statusData } = useAPI(getProjectErrorStatus);
  const { data: techData } = useAPI(getTechnicians);
  const [countryFilter, setCountryFilter] = useState('ALL');

  // Build grouped structure + per-robot ratio + ranking
  const { grouped, ratioRanking, staffingRanking } = useMemo(() => {
    if (!projData?.projects) return { grouped: {}, ratioRanking: {}, staffingRanking: {} };
    const projects = projData.projects;
    const errMap = errData?.projects || {};
    const statusMap = statusData?.projects || {};

    // Count rank-and-file technicians per project — explicitly excluding
    // managers, supervisors, coordinators, and other side-labor roles.
    // The "Robots / Tech" column is meant to gauge field-repair capacity,
    // not the size of the org chart.
    const EXCLUDE_BY_NAME = new Set(['Nadav Arnon', 'Ori Weiss']);
    // Anything containing one of these tokens is treated as non-technician.
    const NON_TECH_ROLE_TOKENS = [
      'manager', 'incharge', 'supervisor', 'coordinator', 'head',
      'engineer', 'safety', 'admin', 'warehouse', 'lead',
    ];
    const isFieldTechnician = (t) => {
      const role = (t.role || '').toLowerCase();
      // Mongo roles: 'technician' (always tech), 'operator' (treat as tech),
      // 'admin' (excluded). CSV roles: 'Technician', 'Plant Incharge', etc.
      if (role === 'technician' || role === 'operator') return true;
      // Anything that looks like a manager/supervisor/etc. → out
      if (NON_TECH_ROLE_TOKENS.some(tok => role.includes(tok))) return false;
      // Default: treat as tech only if role is empty/unknown — be conservative
      return role === '';
    };
    const techCountByPid = {};
    for (const t of (techData?.technicians || [])) {
      const name = (t.fullName || '').toLowerCase();
      if (/\b(test|demo|example)\b/.test(name)) continue;
      if (EXCLUDE_BY_NAME.has(t.fullName)) continue;
      if (!isFieldTechnician(t)) continue;
      for (const pid of (t.projects || [])) {
        techCountByPid[pid] = (techCountByPid[pid] || 0) + 1;
      }
    }

    const result = {};
    const countryProjects = {}; // country -> [{id, ratio, robots_per_tech}]

    for (const p of projects) {
      const country = p.country || 'Other';
      const region = getRegion(country, p.city);

      if (!result[country]) result[country] = {};
      if (!result[country][region]) result[country][region] = [];
      if (!countryProjects[country]) countryProjects[country] = [];

      const errs = errMap[p.id] || { critical: 0, high: 0, low: 0, total: 0 };
      const robots = p.total_robots || 0;
      const importantErrors = errs.critical + errs.high;
      const ratio = robots > 0 ? importantErrors / robots : 0;
      const onError = statusMap[p.id] || 0;
      const techCount = techCountByPid[p.id] || 0;
      const robotsPerTech = techCount > 0 && robots > 0 ? robots / techCount : null;

      const enriched = {
        ...p,
        faults_critical: errs.critical,
        faults_high: errs.high,
        faults_low: errs.low,
        faults_total: errs.total,
        important_errors: importantErrors,
        errors_per_robot: ratio,
        robots_on_error: onError,
        tech_count: techCount,
        robots_per_tech: robotsPerTech,
      };

      result[country][region].push(enriched);
      if (robots > 0) {
        countryProjects[country].push({ id: p.id, ratio, robots_per_tech: robotsPerTech });
      }
    }

    // Sort within regions by errors_per_robot desc
    for (const country of Object.keys(result)) {
      for (const region of Object.keys(result[country])) {
        result[country][region].sort((a, b) => b.errors_per_robot - a.errors_per_robot);
      }
    }

    // Build ranking per country: green (best) / red (worst) for errors/robot
    const ranking = {}; // project_id -> 'green' | 'red' | null
    for (const [country, projs] of Object.entries(countryProjects)) {
      const n = RANK_SIZE[country] || 5;
      const sorted = [...projs].sort((a, b) => a.ratio - b.ratio);
      sorted.slice(0, n).forEach(p => { ranking[p.id] = 'green'; });
      sorted.slice(-n).forEach(p => {
        if (!ranking[p.id]) ranking[p.id] = 'red';
      });
    }

    // Staffing ranking per country: lower robots_per_tech = better staffed
    // (= green); higher = under-staffed (= red). Skip projects with no
    // technicians assigned — they show as gray, not red, since "0 techs"
    // means data is missing on the Technicians page, not a known shortage.
    const staffing = {};
    for (const [country, projs] of Object.entries(countryProjects)) {
      const withTechs = projs.filter(p => p.robots_per_tech !== null);
      if (!withTechs.length) continue;
      const n = Math.max(3, Math.min(RANK_SIZE[country] || 5, Math.floor(withTechs.length / 2)));
      const sorted = [...withTechs].sort((a, b) => a.robots_per_tech - b.robots_per_tech);
      sorted.slice(0, n).forEach(p => { staffing[p.id] = 'green'; });
      sorted.slice(-n).forEach(p => {
        if (!staffing[p.id]) staffing[p.id] = 'red';
      });
    }

    return { grouped: result, ratioRanking: ranking, staffingRanking: staffing };
  }, [projData, errData, statusData, techData]);

  if (loadingProj || loadingErr)
    return <div style={{ padding: 40, color: '#999' }}>Loading projects...</div>;

  const totalProjects = projData?.projects?.length || 0;

  const countriesToShow = countryFilter === 'ALL'
    ? COUNTRY_ORDER
    : COUNTRY_ORDER.filter(c => c === countryFilter);
  const filteredProjectCount = countriesToShow.reduce(
    (sum, c) => sum + Object.values(grouped[c] || {}).flat().length, 0
  );

  return (
    <div>
      <h1 style={styles.title}>Projects</h1>
      <p style={styles.subtitle}>
        {filteredProjectCount} active project{filteredProjectCount === 1 ? '' : 's'}
        {countryFilter !== 'ALL' ? ` in ${countryFilter}` : ' across all regions'}
        <span style={{ marginLeft: 12, fontSize: 12, color: '#b2bec3' }}>Last 30 days</span>
      </p>

      {/* Region filter */}
      <div style={styles.filterBar}>
        <span style={styles.filterLabel}>Region:</span>
        <div style={styles.countryGroup}>
          {COUNTRY_FILTER_OPTIONS.map((opt, i, arr) => {
            const active = countryFilter === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setCountryFilter(opt.key)}
                style={{
                  ...styles.countryBtn,
                  ...(active ? styles.countryBtnActive : {}),
                  borderTopLeftRadius: i === 0 ? 6 : 0,
                  borderBottomLeftRadius: i === 0 ? 6 : 0,
                  borderTopRightRadius: i === arr.length - 1 ? 6 : 0,
                  borderBottomRightRadius: i === arr.length - 1 ? 6 : 0,
                  borderLeft: i === 0 ? '1px solid #dfe6e9' : 'none',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {countriesToShow.map(country => {
        const regions = grouped[country];
        if (!regions) return null;

        const allProjects = Object.values(regions).flat();
        const countryRobots = allProjects.reduce((s, p) => s + (p.total_robots || 0), 0);
        const countryCritical = allProjects.reduce((s, p) => s + p.faults_critical, 0);
        const countryHigh = allProjects.reduce((s, p) => s + p.faults_high, 0);
        const n = RANK_SIZE[country] || 5;

        return (
          <div key={country} style={styles.countrySection}>
            {/* Country header */}
            <div style={styles.countryHeader}>
              <span style={styles.countryFlag}>{COUNTRY_FLAGS[country] || ''}</span>
              <span style={styles.countryName}>{country}</span>
              <span style={styles.countryStats}>
                {allProjects.length} projects | {countryRobots.toLocaleString()} robots
                {countryCritical > 0 && (
                  <span style={styles.countryCritical}> | {countryCritical.toLocaleString()} critical</span>
                )}
                {countryHigh > 0 && (
                  <span style={{ color: '#ffeaa7', fontWeight: 600 }}> | {countryHigh.toLocaleString()} high</span>
                )}
              </span>
            </div>

            {/* Legend */}
            <div style={styles.legend}>
              <span style={{ ...styles.legendDot, background: '#d4edda' }} />
              <span style={styles.legendLabel}>Top {n} lowest errors/robot</span>
              <span style={{ ...styles.legendDot, background: '#ffe0e0', marginLeft: 16 }} />
              <span style={styles.legendLabel}>Top {n} highest errors/robot</span>
            </div>

            {/* Regions within country */}
            {Object.entries(regions)
              .sort(([, a], [, b]) => {
                const aRatio = a.reduce((s, p) => s + p.errors_per_robot, 0) / (a.length || 1);
                const bRatio = b.reduce((s, p) => s + p.errors_per_robot, 0) / (b.length || 1);
                return bRatio - aRatio;
              })
              .map(([region, projects]) => (
                <div key={region} style={styles.regionSection}>
                  <div style={styles.regionHeader}>
                    <span style={styles.regionName}>{region}</span>
                    <span style={styles.regionCount}>{projects.length} projects</span>
                  </div>

                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, textAlign: 'left' }}>Project</th>
                        <th style={styles.th}>Robots</th>
                        <th style={{ ...styles.th, color: '#e17055' }} title="Robots with currentStatus = error (stuck in error state right now)">Currently on Error</th>
                        <th style={{ ...styles.th, color: '#c0392b' }}>
                          Critical <span style={{ fontSize: 10, color: '#95a5a6', fontWeight: 400 }}>30d</span>
                        </th>
                        <th style={{ ...styles.th, color: '#e17055' }}>
                          High <span style={{ fontSize: 10, color: '#95a5a6', fontWeight: 400 }}>30d</span>
                        </th>
                        <th style={{ ...styles.th, color: '#636e72' }}>
                          Low <span style={{ fontSize: 10, color: '#95a5a6', fontWeight: 400 }}>30d</span>
                        </th>
                        <th style={{ ...styles.th, color: '#6c5ce7', fontWeight: 700 }}>
                          Errors / Robot <span style={{ fontSize: 10, color: '#95a5a6', fontWeight: 400 }}>30d</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map(p => {
                        const rank = ratioRanking[p.id];
                        const rowBg = rank === 'green' ? '#f0fff0' : rank === 'red' ? '#fff5f5' : 'transparent';

                        return (
                          <tr
                            key={p.id}
                            style={{ ...styles.row, background: rowBg }}
                            onClick={() => navigate(`/assignments?project=${p.id}`)}
                            onMouseEnter={e => e.currentTarget.style.background = rank === 'green' ? '#e0ffe0' : rank === 'red' ? '#ffe8e8' : '#f8f9fa'}
                            onMouseLeave={e => e.currentTarget.style.background = rowBg}
                          >
                            <td style={styles.tdName}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {rank === 'green' && <span style={styles.rankDotGreen} />}
                                {rank === 'red' && <span style={styles.rankDotRed} />}
                                <div>
                                  <div style={styles.projectName}>{p.name}</div>
                                  <div style={styles.projectCity}>{p.city !== '.' ? p.city : ''}</div>
                                </div>
                              </div>
                            </td>
                            <td style={styles.td}>{p.total_robots || '-'}</td>
                            <td style={styles.td}>
                              {p.robots_on_error > 0 ? (
                                <span style={styles.onErrorBadge}>{p.robots_on_error.toLocaleString()}</span>
                              ) : (
                                <span style={styles.zeroBadge}>0</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {p.faults_critical > 0 ? (
                                <span style={styles.criticalBadge}>{p.faults_critical.toLocaleString()}</span>
                              ) : (
                                <span style={styles.zeroBadge}>0</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {p.faults_high > 0 ? (
                                <span style={styles.highBadge}>{p.faults_high.toLocaleString()}</span>
                              ) : (
                                <span style={styles.zeroBadge}>0</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={p.faults_low > 0 ? styles.lowBadge : styles.zeroBadge}>
                                {p.faults_low > 0 ? p.faults_low.toLocaleString() : 0}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {p.total_robots > 0 ? (
                                <span style={{
                                  ...styles.ratioBadge,
                                  background: rank === 'green' ? '#d4edda' : rank === 'red' ? '#ffe0e0' : '#f0f0f0',
                                  color: rank === 'green' ? '#27ae60' : rank === 'red' ? '#c0392b' : '#2d3436',
                                }}>
                                  {p.errors_per_robot.toFixed(1)}
                                </span>
                              ) : (
                                <span style={styles.zeroBadge}>-</span>
                              )}
                            </td>
                            {/* Robots / Tech column REMOVED 2026-05-03
                                per Yanir — provided no actionable signal
                                in the field (techs cover multiple projects;
                                ratio depends on the Technicians-page
                                assignment policy, not workload). */}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: '#636e72', marginTop: 4, marginBottom: 16 },

  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#636e72',
  },
  countryGroup: {
    display: 'inline-flex',
  },
  countryBtn: {
    padding: '6px 14px',
    border: '1px solid #dfe6e9',
    borderLeft: 'none',
    background: '#fff',
    color: '#636e72',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
    fontWeight: 600,
  },
  countryBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },

  countrySection: { marginBottom: 32 },
  countryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    background: '#1a1a2e',
    color: '#fff',
    borderRadius: '10px 10px 0 0',
  },
  countryFlag: { fontSize: 22 },
  countryName: { fontSize: 18, fontWeight: 700 },
  countryStats: { fontSize: 13, opacity: 0.7, marginLeft: 'auto' },
  countryCritical: { color: '#ff7675', fontWeight: 600 },

  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px',
    background: '#f8f9fa',
    borderBottom: '1px solid #eee',
    fontSize: 11,
    color: '#636e72',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },
  legendLabel: { fontSize: 11 },

  regionSection: {
    marginBottom: 2,
    background: '#fff',
    borderLeft: '3px solid #6c5ce7',
  },
  regionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    background: '#f8f9fa',
    borderBottom: '1px solid #eee',
  },
  regionName: { fontSize: 14, fontWeight: 600, color: '#6c5ce7' },
  regionCount: { fontSize: 12, color: '#999' },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '8px 12px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#999',
    borderBottom: '1px solid #eee',
    textAlign: 'center',
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.15s',
    borderBottom: '1px solid #f5f5f5',
  },
  tdName: { padding: '10px 12px' },
  projectName: { fontSize: 14, fontWeight: 500 },
  projectCity: { fontSize: 11, color: '#999', marginTop: 1 },
  td: { padding: '10px 12px', textAlign: 'center', fontSize: 13 },

  criticalBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
    background: '#ffe0e0',
    color: '#c0392b',
    minWidth: 28,
  },
  onErrorBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
    background: '#ffe8d6',
    color: '#e17055',
    minWidth: 28,
  },
  highBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
    background: '#fff3cd',
    color: '#d68910',
    minWidth: 28,
  },
  lowBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    color: '#636e72',
    minWidth: 28,
  },
  zeroBadge: {
    display: 'inline-block',
    fontSize: 12,
    color: '#ccc',
  },
  ratioBadge: {
    display: 'inline-block',
    padding: '3px 12px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    minWidth: 36,
  },
  rankDotGreen: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#27ae60',
    flexShrink: 0,
  },
  rankDotRed: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#c0392b',
    flexShrink: 0,
  },
};
